import { FormValues } from '../../types/ChampionApplicationFormTypes';
import { ENVIRONMENT, getEnvironment } from '../../utils/environment';

const DEV_GRAPHQL_PATH =
  '/gateway/api/graphql?serviceOverride=upp_service_api=https://upp-service-api.ap-southeast-2.dev.atl-paas.net/api/graphql';
const DEFAULT_GRAPHQL_PATH = '/gateway/api/graphql';

const getGraphqlPath = (): string => {
  const env = getEnvironment();
  return env === ENVIRONMENT.DEV ? DEV_GRAPHQL_PATH : DEFAULT_GRAPHQL_PATH;
};

const UNIFIED_PROFILE_QUERY = `
  query UNIFIED_PROFILE_QUERY($aaid: String!) {
    unified {
      unifiedProfile(aaid: $aaid) {
        ... on UnifiedProfile {
          internalId
        }
        ... on UnifiedQueryError {
          code
          identifier
          message
        }
      }
    }
  }
`;

export const USER_ACCOUNT_QUERY = `
  query USER_ACCOUNT_QUERY($aaid: String!) {
    unified {
      account(aaid: $aaid) {
        ... on UnifiedAccount {
          aaid
          atlassianOneUserId
          emailId
          id
          internalId
          isLinked
          isManaged
          isPrimary
          isProfileOwnerConsented
          khorosUserId
        }
        ... on UnifiedQueryError {
          code
          identifier
          message
        }
      }
    }
  }
`;

export const SUBMIT_CHAMPION_SIGNUP_MUTATION = `
  mutation SUBMIT_CHAMPION_SIGNUP_MUTATION($input: UnifiedChampionSignupInput) {
    champion {
      championSignup {
        submitChampionSignup(input: $input) {
          success
          message
        }
      }
    }
  }
`;

export type ChampionSignupResponseValues = {
  success: boolean;
  message?: string;
};

export type ChampionSignupMutationResponse = {
  data?: {
    champion?: {
      championSignup?: {
        submitChampionSignup?: ChampionSignupResponseValues;
      };
    };
  };
};

export const submitChampionSignup = async (
  input: FormValues,
  baseUrl: string = '',
  Origin?: string,
): Promise<ChampionSignupResponseValues | null> => {
  try {
    // Exclude howHeardEmployee - backend does not support it yet
    const { howHeardEmployee: _excluded, ...inputForBackend } = input;

    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: SUBMIT_CHAMPION_SIGNUP_MUTATION,
        operationName: 'SUBMIT_CHAMPION_SIGNUP_MUTATION',
        variables: { input: inputForBackend },
      }),
    });
    const json: ChampionSignupMutationResponse = await rawResponse.json();
    return json?.data?.champion?.championSignup?.submitChampionSignup ?? null;
  } catch (err) {
    console.error('Error submitting champion signup: ', err);
    return null;
  }
};

export type UserAccountResponse = {
  data?: {
    unified?: {
      account?: {
        aaid?: string;
        atlassianOneUserId?: string | null;
        emailId?: string;
        id?: string;
        internalId?: string;
        isLinked?: boolean;
        isManaged?: boolean;
        isPrimary?: boolean;
        isProfileOwnerConsented?: boolean;
        khorosUserId?: number;
        code?: string;
        identifier?: string;
        message?: string;
      };
    };
  };
};

export const getUserAccountStatuses = async (
  aaid: string,
  baseUrl: string = '',
  Origin?: string,
): Promise<UserAccountResponse | null> => {
  try {
    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: USER_ACCOUNT_QUERY,
        operationName: 'USER_ACCOUNT_QUERY',
        variables: { aaid },
      }),
    });
    return await rawResponse.json();
  } catch (err) {
    console.error('Error fetching user account statuses: ', err);
    return null;
  }
};

export const getProfileInternalId = async (
  aaid: string,
  baseUrl: string = '',
  Origin?: string,
): Promise<string | null> => {
  try {
    const rawResponse = await fetch(`${baseUrl}${getGraphqlPath()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(Origin ? { Origin } : {}),
      },
      body: JSON.stringify({
        query: UNIFIED_PROFILE_QUERY,
        operationName: 'UNIFIED_PROFILE_QUERY',
        variables: { aaid },
      }),
    });

    const json = await rawResponse.json();
    const profile = json?.data?.unified?.unifiedProfile;

    if (!profile || 'code' in profile || !profile.internalId) return null;

    return profile.internalId;
  } catch (err) {
    console.error('Error fetching profile internal ID: ', err);
    return null;
  }
};
